// Admin UI design tokens — white-label neutral skin.
//
// Resolves the `Palette` module alias for the admin build only (see
// process_tsconfig.js: files directly under src/components/admin/ become
// aliases by filename). The Investor UI keeps the original green Fides
// palette via src/components/non_admin/Palette.ts, which re-exports the
// untouched src/components/styles/Palette.ts unchanged.
//
// Same shape as the original palette (tone1 lightest surface through tone5
// darkest, plus active/danger/warning semantic states) so every consumer
// keeps working without changes — only the color values differ. Built from
// a neutral slate scale plus a single indigo accent so it reads as
// "any fund manager's" portal rather than Fides-specific, and is the layer
// a future per-tenant theme config would swap out.
import type { PalleteProps } from '../styles/Palette';

export const Palette: { [key: string]: PalleteProps } = {
  body: {
    background: '#F8FAFC',
    // Consistent default so untagged text doesn't fall back to the raw
    // system-font stack in index.css (which visibly clashes with the Inter
    // headings/buttons elsewhere on the page). Headings still opt into
    // Manrope via FONT_MANROPE as a deliberate display-font pairing.
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  default: {
    background: '#F8FAFC',
    text: '#1E293B',
    buttonBg: '#363B6E',
    inactiveOrCancelButtonBg: '#FFFFFF',
    buttonText: '#FFFFFF',
    inactiveOrCancelButtonText: '#334155',
    graphLine: '#4F46E5',
    border: '#CBD5E1',
    disabledBg: '#F1F5F9',
    disabledText: '#94A3B8',
    activeBg: '#E0E7FF',
    activeText: '#3730A3',
    iconCircle: '#E2E8F0',
  },
  tone1: {
    background: '#FFFFFF',
    text: '#1E293B',
    buttonBg: '#E0E7FF',
    inactiveOrCancelButtonBg: '#FFFFFF',
    buttonText: '#3730A3',
    inactiveOrCancelButtonText: '#1E293B',
    graphLine: '#C7D2FE',
    border: '#E2E8F0',
    disabledBg: '#F1F5F9',
    disabledText: '#94A3B8',
    activeBg: '#EEF2FF',
    activeText: '#3730A3',
    iconCircle: '#F1F5F9',
  },
  tone2: {
    background: '#F1F5F9',
    text: '#1E293B',
    buttonBg: '#4338CA',
    inactiveOrCancelButtonBg: '#FFFFFF',
    buttonText: '#FFFFFF',
    inactiveOrCancelButtonText: '#334155',
    graphLine: '#818CF8',
    border: '#CBD5E1',
    disabledBg: '#E2E8F0',
    disabledText: '#94A3B8',
    activeBg: '#C7D2FE',
    activeText: '#312E81',
    iconCircle: '#E0E7FF',
  },
  tone3: {
    background: '#E2E8F0',
    text: '#1E293B',
    buttonBg: '#4338CA',
    inactiveOrCancelButtonBg: '#FFFFFF',
    buttonText: '#FFFFFF',
    inactiveOrCancelButtonText: '#334155',
    graphLine: '#6366F1',
    border: '#CBD5E1',
    disabledBg: '#CBD5E1',
    disabledText: '#64748B',
    activeBg: '#A5B4FC',
    activeText: '#1E1B4B',
    iconCircle: '#C7D2FE',
  },
  tone4: {
    background: '#1E293B',
    text: '#FFFFFF',
    buttonBg: '#E0E7FF',
    inactiveOrCancelButtonBg: '#1E293B',
    buttonText: '#312E81',
    inactiveOrCancelButtonText: '#C7D2FE',
    graphLine: '#818CF8',
    border: '#334155',
    disabledBg: '#334155',
    disabledText: '#94A3B8',
    activeBg: '#334155',
    activeText: '#FFFFFF',
    iconCircle: '#334155',
  },
  tone5: {
    background: '#0F172A',
    text: '#FFFFFF',
    buttonBg: '#E0E7FF',
    inactiveOrCancelButtonBg: '#0F172A',
    buttonText: '#312E81',
    inactiveOrCancelButtonText: '#A5B4FC',
    graphLine: '#4F46E5',
    border: '#1E293B',
    disabledBg: '#1E293B',
    disabledText: '#64748B',
    activeBg: '#1E293B',
    activeText: '#FFFFFF',
    iconCircle: '#1E293B',
  },
  active: {
    background: '#2563EB',
    text: '#FFFFFF',
    buttonBg: '#1D4ED8',
    inactiveOrCancelButtonBg: '#FFFFFF',
    buttonText: '#FFFFFF',
    inactiveOrCancelButtonText: '#1D4ED8',
    graphLine: '#93C5FD',
    border: '#1D4ED8',
    disabledBg: '#DBEAFE',
    disabledText: '#60A5FA',
  },
  danger: {
    background: '#FEF2F2',
    text: '#7F1D1D',
    buttonBg: '#DC2626',
    buttonText: '#FFFFFF',
    inactiveOrCancelButtonBg: '#FFFFFF',
    inactiveOrCancelButtonText: '#DC2626',
    graphLine: '#F87171',
    border: '#FECACA',
    disabledBg: '#FEE2E2',
    disabledText: '#B91C1C',
    activeBg: '#FCA5A5',
    activeText: '#7F1D1D',
    iconCircle: '#FEE2E2',
  },
  warning: {
    background: '#FFFBEB',
    text: '#78350F',
    buttonBg: '#D97706',
    buttonText: '#FFFFFF',
    inactiveOrCancelButtonBg: '#FFFFFF',
    inactiveOrCancelButtonText: '#D97706',
    graphLine: '#FBBF24',
    border: '#FDE68A',
    disabledBg: '#FEF3C7',
    disabledText: '#B45309',
    activeBg: '#FCD34D',
    activeText: '#78350F',
    iconCircle: '#FEF3C7',
  },
};
